/**
 * Ryan O'Dowd
 * 2018-04-16
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Platform,
  TouchableWithoutFeedback,
  View,
} from 'react-native';
import BackIcon from './BackIcon';
import HeaderIcon from './HeaderIcon';
import React from 'react';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const Header = (_p: HeaderProps) => {
  const props: HeaderProps = {
    ...defaultProps,
    ..._p,
  };

  const styles = hooks.useStyles(getStyles);
  const setHeight = hooks.useSetHeaderHeight();

  let centerComponent = props.centerComponent || (
    <Text
      style={[styles.titleText, {color: props.textColor}, props.titleTextStyle]}
      numberOfLines={props.isMultilineAllowed ? 2 : 1}
      adjustsFontSizeToFit={true}
    >
      {props.title || ''}
    </Text>
  );

  if (props.onTitleLongPress) {
    centerComponent = (
      <TouchableWithoutFeedback onLongPress={() => props.onTitleLongPress()}>
        {centerComponent}
      </TouchableWithoutFeedback>
    );
  }

  const PlaceholderIcon = <HeaderIcon icon='circle' hideIcon={true} onPress={() => null} />;

  const leftItems = [...props.leftItems];
  const rightItems = [...props.rightItems];
  if (Platform.OS !== 'android') {
    for (let l = 0; l < rightItems.length - leftItems.length; l++) {
      leftItems.push(PlaceholderIcon);
    }
    for (let r = 0; r < leftItems.length - rightItems.length; r++) {
      rightItems.unshift(PlaceholderIcon);
    }
  }

  if (leftItems.length === 0 && rightItems.length === 0) {
    // this happens in Practice component sometimes...instead of faking the header height, keep the component, and just add empty placeholder icons
    leftItems.push(PlaceholderIcon);
    rightItems.push(PlaceholderIcon);
  }

  const splitIntoTwoRows = (styles.globalScale >= globals.fontScales.large) && (leftItems.length + rightItems.length > 2) && styles.windowWidth < 600; // @TODO: this could be more dynamic, but it seems to be good right now

  const centerComponentWrapper = (
    <View style={[styles.headerTextWrapper, splitIntoTwoRows && styles.headerTextWrapperSplitRows, props.alwaysCenterTitle && {justifyContent: 'center'}]}>
      {centerComponent}
    </View>
  );

  return (
    <View
      onLayout={(e) => setHeight(e.nativeEvent.layout.height)}
      style={[styles.container, {backgroundColor: props.backgroundColor || 'transparent'}]}
    >
      <View style={[styles.headerInner, splitIntoTwoRows && styles.splitRowsWrapper]}>
        {splitIntoTwoRows && centerComponentWrapper}

        <View style={styles.secondRow}>
          <View style={styles.leftItems}>
            {leftItems.map((li, i) => <React.Fragment key={i}>{li}</React.Fragment>)}
          </View>

          {!splitIntoTwoRows && centerComponentWrapper}

          <View style={styles.rightItems}>
            {rightItems.map((ri, i) => <React.Fragment key={i}>{ri}</React.Fragment>)}
          </View>
        </View>
      </View>
    </View>
  );
};

class HeaderProps {
  textColor?: string = '#ffffff'; // @TODO: styles.colors.white;
  backgroundColor?: string;
  centerComponent?: React.ReactNode;
  leftItems?: React.ReactNode[] = [];
  rightItems?: React.ReactNode[] = [];
  title?: string;
  alwaysCenterTitle?: boolean;
  onTitleLongPress?: () => void;
  isMultilineAllowed?: boolean;
  titleTextStyle?: any;
};
const defaultProps = new HeaderProps();

Header.Icon = HeaderIcon;
Header.BackIcon = BackIcon;
export default Header;
